import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { isAndroidPlatform } from "@src/appV2/lib";
import { type Location } from "@src/appV2/lib/GoogleMaps";
import constate from "constate";
import { useHistory } from "react-router-dom";

import { constructAppleMapUrl } from "./constructAppleMapUrl";
import { constructGoogleMapUrl } from "./constructGoogleMapUrl";

interface OpenGeoLocationProps {
  location: Location | undefined;
  formattedAddress?: string;
  name?: string;
  bottomSheetRoutePath?: string;
}

/**
 * Opening map on actual device differs from Android and iOS.
 * Since there is no way to natively open preferred map app on iOS, we use OpenGeoLocationBottomSheet
 * to let the user choose between Google Maps and Apple Maps.
 * On Android, we can open the map directly by providing the `href` to the google maps app.
 *
 * This hook returns both `href` and `onClick` handlers that you can assign to your button.
 * Depending on the platform it will either assign href to the maps url or set up an onClick handler
 * to open the bottom sheet.
 *
 * We also support route-based modal for the geolocation bottomsheet. To use this,
 * pass the bottomSheetRoutePath to the hook so it knows the path to navigate to when the modal is opened.
 *
 * @example Usage:
 *   const { href, onClick } = useOpenGeoLocationContext();
 *
 *   return <Button href={href} onClick={onClick} />;
 */
export function useOpenGeoLocation(props: OpenGeoLocationProps) {
  const { location, formattedAddress, bottomSheetRoutePath } = props;
  const modalState = useModalState();
  const history = useHistory();

  if (!location) {
    return { googleMapsHref: undefined, appleMapsHref: undefined, modalState, onClick: undefined };
  }

  const googleMapsHref = constructGoogleMapUrl({ location, formattedAddress });
  const appleMapsHref = constructAppleMapUrl({ location, formattedAddress });

  let href: string | undefined;
  let onClick: (() => void) | undefined;

  if (isAndroidPlatform()) {
    href = googleMapsHref;
    onClick = undefined;
  } else {
    href = undefined;
    onClick = () => {
      if (isDefined(bottomSheetRoutePath)) {
        history.push(bottomSheetRoutePath);
        return;
      }

      modalState.openModal();
    };
  }

  return { googleMapsHref, appleMapsHref, href, modalState, onClick, formattedAddress };
}

export const [OpenGeoLocationProvider, useOpenGeoLocationContext] = constate(useOpenGeoLocation);
